import { CarTradeInCvna } from '@carvana/ds-icons';
import React from 'react';
import { StepName } from '|/app/services';
import LinkButton from '|/components/carvana-ds/link-button';
import currency from '|/helpers/currency';
import useNavigateToStep from '|/hooks/use-navigate-to-step';
import { AmountRow } from './amount-row';
import PriceSection from './price-section';
import { PriceDetailsView } from '../derive-price-details';
import styles from '../price-details.module.css';

interface TradeInSectionProps {
  view: PriceDetailsView;
  /**
   * Opens the trade-in details modal, which owns the payoff breakdown for every scenario —
   * the section itself no longer expands one inline (the design puts the
   * "Paying off your existing loan" panel inside the modal).
   */
  onViewTradeInDetails?: () => void;
}

const TradeInSection = ({ view, onViewTradeInDetails }: TradeInSectionProps) => {
  const navigateToStep = useNavigateToStep();
  const { tradeIn } = view;

  const icon = <CarTradeInCvna size={16} color="var(--icon-strong)" />;

  // A declined trade drops the breakdown and the total row for a single prompt.
  if (tradeIn.isDeclined) {
    return (
      <PriceSection icon={icon} title="Trade-in" testId="pp_price-details_trade-in">
        <div className={styles.tradeGroup}>
          <p className={styles.payoffNote} data-testid="pp_price-details_trade-in-declined">
            {tradeIn.declinedCopy}
          </p>
          <LinkButton
            size="xs"
            underline={false}
            className={styles.tradeGroupAction}
            onClick={() => {
              navigateToStep(StepName.TradeIn);
            }}
            data-analytics-id="pp_review_order_summary_edit_tradein"
          >
            Add trade-in
          </LinkButton>
        </div>
      </PriceSection>
    );
  }

  return (
    <PriceSection icon={icon} title="Trade-in" testId="pp_price-details_trade-in">
      <div className={styles.tradeGroup}>
        <AmountRow
          label={tradeIn.label}
          amount={`${tradeIn.amount < 0 ? '-' : ''}${currency(Math.abs(tradeIn.amount))}`}
          positive={!tradeIn.isNegativeEquity}
          testId="pp_price-details_trade-in-applied"
        />
        <LinkButton
          size="xs"
          underline={false}
          className={styles.tradeGroupAction}
          onClick={() => {
            onViewTradeInDetails?.();
          }}
          data-analytics-id="pp_review_order_summary_edit_tradein"
          data-testid="pp_price-details_trade-in-toggle"
        >
          View details
        </LinkButton>
      </div>

      <AmountRow
        label="Total price w/ trade-in"
        amount={currency(view.totalPriceWithTradeIn)}
        emphasis="total"
      />
    </PriceSection>
  );
};

export default TradeInSection;
